import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Globe, ChevronDown } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { CONFIG } from '../data/villaData';
import MoonlitLogo from './MoonlitLogo';

export default function Header({ onBookClick }) {
  const { lang, setLang, t, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItems = [
    { key: 'home', href: '#home' },
    { key: 'about', href: '#about' },
    { key: 'villa', href: '#villa' },
    { key: 'amenities', href: '#amenities' },
    { key: 'location', href: '#location' },
    { key: 'contact', href: '#contact' }
  ];

  const currentLang = languages.find(l => l.code === lang);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-md py-2' : 'bg-transparent py-4'}`}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="#home" onClick={(e) => { e.preventDefault(); scrollTo('#home'); }} className="flex items-center gap-3">
          <MoonlitLogo
            mode={scrolled ? 'light' : 'dark'}
            className={`transition-all duration-500 ${scrolled ? 'h-14' : 'h-20'} w-auto`}
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map(item => (
            <a
              key={item.key}
              href={item.href}
              onClick={(e) => { e.preventDefault(); scrollTo(item.href); }}
              className={`text-sm font-medium tracking-wide uppercase transition-colors hover:text-[#b8945e] ${scrolled ? 'text-stone-800' : 'text-white drop-shadow'}`}
            >
              {t.nav[item.key]}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Language switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors ${scrolled ? 'text-stone-800 hover:bg-stone-100' : 'text-white hover:bg-white/10'}`}
            >
              <Globe size={16} />
              <span className="hidden sm:inline">{currentLang?.flag} {currentLang?.code.toUpperCase()}</span>
              <ChevronDown size={14} />
            </button>
            {langOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
                <div className="absolute right-0 top-full mt-2 w-48 bg-white shadow-xl rounded-lg overflow-hidden z-50 border border-stone-200">
                  {languages.map(l => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#faf6ef] flex items-center gap-3 transition-colors ${lang === l.code ? 'bg-[#faf6ef] text-[#b8945e] font-semibold' : 'text-stone-700'}`}
                    >
                      <span className="text-lg">{l.flag}</span>
                      <span>{l.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <a
            href={`tel:+${CONFIG.whatsapp}`}
            className={`hidden md:flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${scrolled ? 'text-stone-800 hover:bg-stone-100' : 'text-white hover:bg-white/10'}`}
          >
            <Phone size={16} />
            <span>{CONFIG.whatsappDisplay}</span>
          </a>

          <button
            onClick={onBookClick}
            className="hidden md:inline-flex items-center px-5 py-2.5 bg-[#b8945e] hover:bg-[#a07d4a] text-white text-sm font-semibold rounded-full transition-colors shadow-md"
          >
            {t.nav.book}
          </button>

          <button onClick={() => setOpen(!open)} className={`lg:hidden p-2 rounded-md ${scrolled ? 'text-stone-800' : 'text-white'}`}>
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-stone-200 shadow-lg">
          <nav className="flex flex-col py-4 px-6">
            {navItems.map(item => (
              <a
                key={item.key}
                href={item.href}
                onClick={(e) => { e.preventDefault(); scrollTo(item.href); }}
                className="py-3 text-stone-800 text-sm font-medium uppercase tracking-wide border-b border-stone-100 hover:text-[#b8945e]"
              >
                {t.nav[item.key]}
              </a>
            ))}
            <button onClick={() => { setOpen(false); onBookClick(); }} className="mt-4 px-5 py-3 bg-[#b8945e] hover:bg-[#a07d4a] text-white text-sm font-semibold rounded-full transition-colors">
              {t.nav.book}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
